import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AuditFilters = ({ onFiltersChange }) => {
  const [filters, setFilters] = useState({
    dateRange: { start: '', end: '' },
    departments: [],
    assetCategories: [],
    verificationStatus: '',
    priority: '',
    assignedTo: '',
    location: ''
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const departmentOptions = [
    { value: 'it', label: 'IT Department' },
    { value: 'finance', label: 'Finance' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'operations', label: 'Operations' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'sales', label: 'Sales' }
  ];

  const assetCategoryOptions = [
    { value: 'laptops', label: 'Laptops' },
    { value: 'desktops', label: 'Desktop Computers' },
    { value: 'servers', label: 'Servers' },
    { value: 'printers', label: 'Printers' },
    { value: 'monitors', label: 'Monitors' },
    { value: 'networking', label: 'Network Equipment' },
    { value: 'mobile', label: 'Mobile Devices' },
    { value: 'peripherals', label: 'Peripherals' }
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending Verification' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'verified', label: 'Verified' },
    { value: 'exception', label: 'Exception' },
    { value: 'escalated', label: 'Escalated' }
  ];

  const priorityOptions = [
    { value: '', label: 'All Priorities' },
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  const assignedToOptions = [
    { value: '', label: 'All Auditors' },
    { value: 'john-smith', label: 'John Smith' },
    { value: 'sarah-johnson', label: 'Sarah Johnson' },
    { value: 'mike-davis', label: 'Mike Davis' },
    { value: 'lisa-chen', label: 'Lisa Chen' },
    { value: 'tom-wilson', label: 'Tom Wilson' }
  ];

  const locationOptions = [
    { value: '', label: 'All Locations' },
    { value: 'floor-1', label: 'Floor 1' },
    { value: 'floor-2', label: 'Floor 2' },
    { value: 'floor-3', label: 'Floor 3' },
    { value: 'data-center', label: 'Data Center' },
    { value: 'warehouse', label: 'Warehouse' },
    { value: 'remote', label: 'Remote Locations' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleDateRangeChange = (type, value) => {
    const newDateRange = { ...filters?.dateRange, [type]: value };
    const newFilters = { ...filters, dateRange: newDateRange };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      dateRange: { start: '', end: '' },
      departments: [],
      assetCategories: [],
      verificationStatus: '',
      priority: '',
      assignedTo: '',
      location: ''
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.dateRange?.start || filters?.dateRange?.end) count++;
    if (filters?.departments?.length > 0) count++;
    if (filters?.assetCategories?.length > 0) count++;
    if (filters?.verificationStatus) count++;
    if (filters?.priority) count++;
    if (filters?.assignedTo) count++;
    if (filters?.location) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <div className="bg-card border border-border rounded-lg shadow-subtle">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-card-foreground">Audit Scope Filters</h3>
            {activeFilterCount > 0 && (
              <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full font-medium">
                {activeFilterCount} active
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={16} className="mr-2" />
                Clear All
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} className="mr-2" />
              {isExpanded ? 'Collapse' : 'Expand'} Filters
            </Button>
          </div>
        </div>
      </div>
      {/* Quick Filters - Always Visible */}
      <div className="p-4 border-b border-border">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <Select
            label="Verification Status"
            options={statusOptions}
            value={filters?.verificationStatus}
            onChange={(value) => handleFilterChange('verificationStatus', value)}
            placeholder="Select status"
          />
          <Select
            label="Priority"
            options={priorityOptions}
            value={filters?.priority}
            onChange={(value) => handleFilterChange('priority', value)}
            placeholder="Select priority"
          />
          <Select
            label="Assigned To"
            options={assignedToOptions}
            value={filters?.assignedTo}
            onChange={(value) => handleFilterChange('assignedTo', value)}
            placeholder="Select auditor"
          />
          <Select
            label="Location"
            options={locationOptions}
            value={filters?.location}
            onChange={(value) => handleFilterChange('location', value)}
            placeholder="Select location"
          />
          <div className="flex items-end">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => console.log('Applying quick filters')}
            >
              <Icon name="Filter" size={16} className="mr-2" />
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
      {/* Advanced Filters - Expandable */}
      {isExpanded && (
        <div className="p-4 space-y-6">
          {/* Date Range */}
          <div>
            <h4 className="text-sm font-medium text-card-foreground mb-3">Date Range</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Start Date"
                type="date"
                value={filters?.dateRange?.start}
                onChange={(e) => handleDateRangeChange('start', e?.target?.value)}
              />
              <Input
                label="End Date"
                type="date"
                value={filters?.dateRange?.end}
                onChange={(e) => handleDateRangeChange('end', e?.target?.value)}
              />
            </div>
          </div>

          {/* Multi-Select Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Select
                label="Departments"
                options={departmentOptions}
                value={filters?.departments}
                onChange={(value) => handleFilterChange('departments', value)}
                multiple
                searchable
                placeholder="Select departments"
              />
            </div>
            <div>
              <Select
                label="Asset Categories"
                options={assetCategoryOptions}
                value={filters?.assetCategories}
                onChange={(value) => handleFilterChange('assetCategories', value)}
                multiple
                searchable
                placeholder="Select asset types"
              />
            </div>
          </div>

          {/* Preset Filter Options */}
          <div>
            <h4 className="text-sm font-medium text-card-foreground mb-3">Quick Presets</h4>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const presetFilters = {
                    ...filters,
                    verificationStatus: 'pending',
                    priority: 'high'
                  };
                  setFilters(presetFilters);
                  onFiltersChange(presetFilters);
                }}
              >
                High Priority Pending
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const presetFilters = {
                    ...filters,
                    verificationStatus: 'exception'
                  };
                  setFilters(presetFilters);
                  onFiltersChange(presetFilters);
                }}
              >
                All Exceptions
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const presetFilters = {
                    ...filters,
                    assetCategories: ['laptops', 'desktops'],
                    verificationStatus: 'pending'
                  };
                  setFilters(presetFilters);
                  onFiltersChange(presetFilters);
                }}
              >
                Pending Computers
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                  const presetFilters = {
                    ...filters,
                    dateRange: {
                      start: weekAgo?.toISOString()?.split('T')?.[0],
                      end: today?.toISOString()?.split('T')?.[0]
                    }
                  };
                  setFilters(presetFilters);
                  onFiltersChange(presetFilters);
                }}
              >
                Last 7 Days
              </Button>
            </div>
          </div>

          {/* Save/Load Filter Sets */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm">
                <Icon name="Save" size={16} className="mr-2" />
                Save Filter Set
              </Button>
              <Button variant="ghost" size="sm">
                <Icon name="FolderOpen" size={16} className="mr-2" />
                Load Saved Filters
              </Button>
            </div>
            <div className="text-sm text-muted-foreground">
              {activeFilterCount > 0 ? `${activeFilterCount} filters applied` : 'No filters applied'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditFilters;